import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import {
  Employee,
  EmployeeSearchData,
  EmployeeNotes,
  EmployeeEmployments,
  EmergencyContact,
  EmployeeQualification,
} from '../../models/employee.model';
import { revertAll, RootState } from '../../store';

export const fetchEmployees = createAsyncThunk(
  'companies/fetchEmployees',
  async ({ company_id }: EmployeeSearchData, { rejectWithValue }) => {
    if (company_id === undefined) {
      return rejectWithValue('Company ID is required');
    }
    const response = await NWClient.list('employee', { company_id });
    return response;
  }
);

export const fetchEmployeeById = createAsyncThunk(
  'companies/fetchEmployeeById',
  async (employeeId: number, { rejectWithValue }) => {
    if (employeeId === undefined) {
      return rejectWithValue('Employee ID is required');
    }
    const response = await NWClient.get('employee', employeeId);
    return response;
  }
);

export const fetchEmployeeNotes = createAsyncThunk(
  'employees/fetchEmployeeNotes',
  async (employeeId: number, { rejectWithValue }) => {
    if (employeeId === undefined) {
      return rejectWithValue('Employee ID is required');
    }
    try {
      const response = (await NWClient.get('employee', employeeId)) as Employee;
      return { employeeId, notes: response.notes as EmployeeNotes[] };
    } catch (error) {
      return rejectWithValue('Failed to fetch employee notes');
    }
  }
);

export const deleteEmployeeNote = createAsyncThunk(
  'employees/deleteEmployeeNote',
  async ({ employeeId, noteId }: { employeeId: number; noteId: number }, { dispatch }) => {
    try {
      await NWClient.delete(`employee/${employeeId}/notes`, noteId);
      // After successful deletion, fetch updated notes to refresh state
      await dispatch(fetchEmployeeNotes(employeeId));
      return { success: true };
    } catch (error) {
      // Handle error appropriately
      return { success: false, error };
    }
  }
);

export const updateEmployee = createAsyncThunk(
  'employees/updateEmployee',
  async (
    payload:
      | Partial<Employee>
      | { employeeId: number; employmentId?: number; employmentData: Partial<EmployeeEmployments> }
      | {
          employeeId: number;
          emergencyContactData: Partial<EmergencyContact>;
          emergencyContactId?: number;
        }
      | {
          employeeId: number;
          qualificationData: Partial<EmployeeQualification>;
          qualificationId?: number;
        },
    { rejectWithValue }
  ) => {
    // Check if this is an employment update
    if ('employeeId' in payload && 'employmentData' in payload) {
      const { employeeId, employmentId, employmentData } = payload;

      if (!employeeId) {
        return rejectWithValue('Employee ID is required');
      }

      try {
        let response;
        if (employmentId) {
          // Update existing employment
          response = await NWClient.patch(
            `employee/${employeeId}/employments/${employmentId}/`,
            null,
            employmentData
          );
        } else {
          // Create new employment
          response = await NWClient.post(
            `employee/${employeeId}/employments`,
            employmentData,
            true
          );
        }
        return {
          type: 'employment',
          employeeId,
          employment: response,
        };
      } catch (error) {
        return rejectWithValue('Failed to update employment');
      }
    }
    // Emergency Contact update
    else if ('employeeId' in payload && 'emergencyContactData' in payload) {
      const { employeeId, emergencyContactData, emergencyContactId } = payload;

      if (!employeeId) {
        return rejectWithValue('Employee ID is required');
      }

      try {
        let response;
        if (emergencyContactId) {
          response = await NWClient.put(
            `employee/${employeeId}/emergency_contact`,
            emergencyContactId,
            emergencyContactData,
            true,
            true
          );
        } else {
          response = await NWClient.post(
            `employee/${employeeId}/emergency_contact`,
            emergencyContactData,
            true
          );
        }
        return {
          type: 'emergencyContact',
          employeeId,
          emergencyContact: response,
        };
      } catch (error) {
        return rejectWithValue('Failed to update emergency contact');
      }
    }
    // Qualification update
    else if ('employeeId' in payload && 'qualificationData' in payload) {
      const { employeeId, qualificationData, qualificationId } = payload;

      if (!employeeId) {
        return rejectWithValue('Employee ID is required');
      }

      try {
        let response;
        if (qualificationId) {
          response = await NWClient.patch(
            `employee/${employeeId}/qualifications/${qualificationId}/`,
            null,
            qualificationData
          );
        } else {
          response = await NWClient.post(
            `employee/${employeeId}/qualifications`,
            qualificationData,
            true
          );
        }
        return {
          type: 'qualification',
          employeeId,
          qualification: response,
        };
      } catch (error) {
        return rejectWithValue('Failed to update qualification');
      }
    }
    // Regular employee update
    else {
      if (!payload.id) {
        return rejectWithValue('Employee ID is required');
      }

      try {
        const response = await NWClient.patch(`employee/${payload.id}/`, null, payload);
        return {
          type: 'employee',
          employee: response,
        };
      } catch (error) {
        return rejectWithValue('Failed to update employee');
      }
    }
  }
);

export interface EmployeesState {
  employeesList: Employee[];
  status: string;
  error: string;
  notes: EmployeeNotes[];
  editingEmployee: Partial<Employee> | null;
  editingEmployment: Partial<EmployeeEmployments> | null;
  editingEmergencyContact: Partial<EmergencyContact> | null;
  editingQualification: Partial<EmployeeQualification> | null;
}

const employeesArray: Employee[] = [];
const initialState: EmployeesState = {
  employeesList: employeesArray,
  notes: [],
  status: 'idle',
  error: '',
  editingEmployee: null,
  editingEmployment: null,
  editingEmergencyContact: null,
  editingQualification: null,
};

const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    clearEmployeeNotes(state) {
      state.notes = []; // Reset notes to an empty array
    },
    setEditingEmployee(state, action: PayloadAction<Partial<Employee>>) {
      state.editingEmployee = action.payload;
    },
    updateEditingEmployee(state, action: PayloadAction<{ field: keyof Employee; value: any }>) {
      if (state.editingEmployee) {
        const { field, value } = action.payload;
        state.editingEmployee = {
          ...state.editingEmployee,
          [field]: value,
        };
      }
    },
    clearEditingEmployee(state) {
      state.editingEmployee = null;
    },
    setEditingEmployment(state, action: PayloadAction<Partial<EmployeeEmployments>>) {
      state.editingEmployment = action.payload;
    },
    updateEditingEmployment(
      state,
      action: PayloadAction<{ field: keyof EmployeeEmployments; value: any }>
    ) {
      if (state.editingEmployment) {
        const { field, value } = action.payload;
        state.editingEmployment = {
          ...state.editingEmployment,
          [field]: value,
        };
      }
    },
    clearEditingEmployment(state) {
      state.editingEmployment = null;
    },
    setEditingEmergencyContact(state, action: PayloadAction<Partial<EmergencyContact>>) {
      state.editingEmergencyContact = action.payload;
    },
    updateEditingEmergencyContact(
      state,
      action: PayloadAction<{ field: keyof EmergencyContact; value: any }>
    ) {
      if (state.editingEmergencyContact) {
        const { field, value } = action.payload;
        state.editingEmergencyContact = {
          ...state.editingEmergencyContact,
          [field]: value,
        };
      }
    },
    clearEditingEmergencyContact(state) {
      state.editingEmergencyContact = null;
    },
    setEditingQualification(state, action: PayloadAction<Partial<EmployeeQualification>>) {
      state.editingQualification = action.payload;
    },
    updateEditingQualification(
      state,
      action: PayloadAction<{ field: keyof EmployeeQualification; value: any }>
    ) {
      if (state.editingQualification) {
        const { field, value } = action.payload;
        state.editingQualification = {
          ...state.editingQualification,
          [field]: value,
        };
      }
    },
    clearEditingQualification(state) {
      state.editingQualification = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.employeesList = [...action.payload];
          state.status = 'succeeded';
        }
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchEmployeeNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEmployeeNotes.fulfilled, (state, action) => {
        const { employeeId, notes } = action.payload;
        state.notes = notes;
        state.status = 'succeeded';
      })
      .addCase(fetchEmployeeNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch employee notes';
      })
      .addCase(deleteEmployeeNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteEmployeeNote.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(deleteEmployeeNote.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to delete employee note';
      })
      .addCase(updateEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        updateEmployee.fulfilled,
        (
          state,
          action: PayloadAction<{
            type: string;
            employeeId?: number;
            employment?: any;
            emergencyContact?: any;
            qualification?: any;
            employee?: Employee;
          }>
        ) => {
          state.status = 'succeeded';

          // Handle different response types
          if (action.payload.type === 'employment') {
            // Update the employment in the employee object
            const { employeeId, employment } = action.payload;
            const employeeIndex = state.employeesList.findIndex((emp) => emp.id === employeeId);

            if (employeeIndex !== -1) {
              const employee = state.employeesList[employeeIndex];
              const employmentIndex =
                employee.employments?.findIndex((emp) => emp.id === employment.id) ?? -1;

              if (employmentIndex !== -1 && employee.employments) {
                employee.employments[employmentIndex] = employment;
              } else if (employee.employments) {
                employee.employments.push(employment); // Add new employment
              } else {
                employee.employments = [employment]; // Create array if doesn't exist
              }
            }

            state.editingEmployment = null;
          } else if (action.payload.type === 'emergencyContact') {
            // Update emergency contact in the employee object
            const { employeeId, emergencyContact } = action.payload;
            const employeeIndex = state.employeesList.findIndex((emp) => emp.id === employeeId);

            if (employeeIndex !== -1) {
              state.employeesList[employeeIndex].emergency_contact = emergencyContact;
            }
            state.editingEmergencyContact = null;
          } else if (action.payload.type === 'qualification') {
            // Update qualification in the employee object
            const { employeeId, qualification } = action.payload;
            const employeeIndex = state.employeesList.findIndex((emp) => emp.id === employeeId);

            if (employeeIndex !== -1) {
              const employee = state.employeesList[employeeIndex];
              const qualificationIndex = employee.qualifications?.findIndex(
                (q) => q.id === qualification.id
              );

              if (
                qualificationIndex !== -1 &&
                qualificationIndex !== undefined &&
                employee.qualifications
              ) {
                employee.qualifications[qualificationIndex] = qualification;
              } else if (employee.qualifications) {
                employee.qualifications.push(qualification);
              } else {
                employee.qualifications = [qualification];
              }
            }
            state.editingQualification = null;
          } else {
            // Update the employee in the employeesList
            const updatedEmployee = action.payload.employee as Employee;
            const index = state.employeesList.findIndex((emp) => emp.id === updatedEmployee.id);
            if (index !== -1) {
              state.employeesList[index] = updatedEmployee;
            }
            state.editingEmployee = null;
          }
        }
      )
      .addCase(updateEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update employee';
      })
      .addCase(revertAll, () => initialState);
  },
});

export const {
  clearEmployeeNotes,
  setEditingEmployee,
  updateEditingEmployee,
  clearEditingEmployee,
  setEditingEmployment,
  updateEditingEmployment,
  clearEditingEmployment,
  setEditingEmergencyContact,
  updateEditingEmergencyContact,
  clearEditingEmergencyContact,
  setEditingQualification,
  updateEditingQualification,
  clearEditingQualification,
} = employeesSlice.actions;

export default employeesSlice.reducer;

export const selectAllEmployees = (state: RootState) => state.employees.employeesList;
export const selectEmployeeById = (state: RootState, employeeId: number) =>
  state.employees.employeesList.find((item) => item.id === employeeId);
export const selectEmployeeNotes = (state: RootState) => state.employees.notes;
export const selectEditingEmployee = (state: RootState) => state.employees.editingEmployee;
export const selectEmployeesStatus = (state: RootState) => state.employees.status;
export const selectEditingEmployment = (state: RootState) => state.employees.editingEmployment;
export const selectEditingEmergencyContact = (state: RootState) =>
  state.employees.editingEmergencyContact;
export const selectEditingQualification = (state: RootState) =>
  state.employees.editingQualification;
